import type { DocumentMetadataUserEditValues } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  getCantileverDocumentEntryId,
  getSystemDocumentBqId,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  mergeDocumentMetadataValues,
  preferUserEditValue,
} from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import {
  normalizeRevisionFamilyKeyPart,
  type RevisionFamilyMemberView,
} from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { compareRevisionCodes } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'

export type DocumentRevisionFamilyMember = RevisionFamilyMemberView & {
  systemDocument: SystemDocumentEntry
}

export function documentRevisionFamilyKey(
  drawingNumber: string | null | undefined
): string | undefined {
  const drawing = normalizeRevisionFamilyKeyPart(drawingNumber)
  if (!drawing) return undefined
  return drawing
}

/** Family key from system document + optional user-edit overlay. */
export function effectiveDocumentRevisionFamilyKey(
  systemDocument: SystemDocumentEntry | undefined,
  editValues: DocumentMetadataUserEditValues | undefined
): string | undefined {
  if (!systemDocument) return undefined
  const merged = mergeDocumentMetadataValues(
    systemDocument.entry.data,
    editValues
  )
  return documentRevisionFamilyKey(merged.drawing_number)
}

export function filterDocumentsInRevisionFamily(
  documents: readonly SystemDocumentEntry[],
  drawingNumber: string | null | undefined
): SystemDocumentEntry[] {
  const familyKey = documentRevisionFamilyKey(drawingNumber)
  if (!familyKey) return []
  return documents.filter((document) => {
    const key = documentRevisionFamilyKey(document.entry.data.drawing_number)
    return key === familyKey
  })
}

/** Keep members whose effective family key still matches (handles leave-family). */
export function filterMembersByEffectiveDocumentRevisionFamilyKey({
  members,
  currentFamilyKey,
  editValuesByDocumentEntryId,
}: {
  members: readonly DocumentRevisionFamilyMember[]
  currentFamilyKey: string | undefined
  editValuesByDocumentEntryId: ReadonlyMap<
    string,
    DocumentMetadataUserEditValues | undefined
  >
}): DocumentRevisionFamilyMember[] {
  if (!currentFamilyKey) return []
  return members.filter((member) => {
    const key = effectiveDocumentRevisionFamilyKey(
      member.systemDocument,
      editValuesByDocumentEntryId.get(member.memberEntryId)
    )
    return key === currentFamilyKey
  })
}

function compareNullableStringsDesc(
  first: string | null | undefined,
  second: string | null | undefined
): number {
  return (second ?? '').localeCompare(first ?? '')
}

/** Latest-first: revision date desc, then revision code desc. */
export function sortDocumentsByRevisionLatestFirst(
  documents: readonly SystemDocumentEntry[]
): SystemDocumentEntry[] {
  return [...documents].sort((a, b) => {
    const dateCompare = compareNullableStringsDesc(
      a.entry.data.revision_date,
      b.entry.data.revision_date
    )
    if (dateCompare !== 0) return dateCompare

    const codeA = a.entry.data.revision_code?.trim() ?? ''
    const codeB = b.entry.data.revision_code?.trim() ?? ''
    if (codeA && codeB) {
      return -compareRevisionCodes(codeA, codeB)
    }
    return compareNullableStringsDesc(codeA, codeB)
  })
}

export function buildDocumentRevisionFamilyMembers({
  documents,
  currentBqDocumentId,
}: {
  documents: readonly SystemDocumentEntry[]
  currentBqDocumentId: string | undefined
}): DocumentRevisionFamilyMember[] {
  const sorted = sortDocumentsByRevisionLatestFirst(documents)
  const members: DocumentRevisionFamilyMember[] = []

  for (const document of sorted) {
    const bqDocumentId = getSystemDocumentBqId(document)
    if (!bqDocumentId) continue

    const totalPages = document.entry.data.total_pages
    members.push({
      bqDocumentId,
      pageNum: 1,
      numPages:
        typeof totalPages === 'number' && totalPages > 0 ? totalPages : null,
      revisionCode: document.entry.data.revision_code ?? null,
      revisionDate: document.entry.data.revision_date ?? null,
      filename: document.entry.data.filename ?? null,
      systemEntryId: document.entry.id,
      cantileverDocumentEntryId: getCantileverDocumentEntryId(document),
      memberEntryId: document.entry.id,
      systemDocument: document,
      isCurrent:
        currentBqDocumentId !== undefined &&
        bqDocumentId === currentBqDocumentId,
    })
  }

  return members
}

/** Apply document user-edits onto family member revision display fields. */
export function applyDocumentUserEditsToRevisionFamilyMembers({
  members,
  editsByDocumentEntryId,
}: {
  members: readonly DocumentRevisionFamilyMember[]
  editsByDocumentEntryId: ReadonlyMap<
    string,
    DocumentMetadataUserEditValues | undefined
  >
}): DocumentRevisionFamilyMember[] {
  if (editsByDocumentEntryId.size === 0) {
    return [...members]
  }
  const next = members.map((member) => {
    const editValues = editsByDocumentEntryId.get(member.memberEntryId)
    if (editValues === undefined) {
      return member
    }
    const revisionCode =
      preferUserEditValue(editValues.revision_code, member.revisionCode) ?? null
    const revisionDate =
      preferUserEditValue(editValues.revision_date, member.revisionDate) ?? null
    if (
      revisionCode === member.revisionCode &&
      revisionDate === member.revisionDate
    ) {
      return member
    }
    return {
      ...member,
      revisionCode,
      revisionDate,
    }
  })
  return sortDocumentRevisionFamilyMembersLatestFirst(next)
}

function sortDocumentRevisionFamilyMembersLatestFirst(
  members: readonly DocumentRevisionFamilyMember[]
): DocumentRevisionFamilyMember[] {
  return [...members].sort((a, b) => {
    const dateCompare = compareNullableStringsDesc(
      a.revisionDate,
      b.revisionDate
    )
    if (dateCompare !== 0) return dateCompare
    const codeA = a.revisionCode?.trim() ?? ''
    const codeB = b.revisionCode?.trim() ?? ''
    if (codeA && codeB) {
      return -compareRevisionCodes(codeA, codeB)
    }
    return compareNullableStringsDesc(codeA, codeB)
  })
}
